import React, { useEffect, useState } from "react";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import "./FloatingConsultationCta.css";

const consultationPaths = new Set(["/consultations", "/future-teacher"]);

const FloatingConsultationCta = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasPassedRelevantSection, setHasPassedRelevantSection] = useState(false);
  const isHome = location.pathname === "/" || location.pathname === "/start";
  const isConsultationPage = consultationPaths.has(location.pathname);

  useEffect(() => {
    const trackedSectionId = isHome
      ? "home-services"
      : isConsultationPage
      ? "consultation-form"
      : "";

    if (!trackedSectionId) {
      setHasPassedRelevantSection(false);
      return;
    }

    const updateVisibility = () => {
      const section = document.getElementById(trackedSectionId);
      if (!section) {
        setHasPassedRelevantSection(false);
        return;
      }

      const rect = section.getBoundingClientRect();
      const shouldShow = isHome
        ? rect.top <= window.innerHeight - 80
        : rect.bottom <= 80;
      setHasPassedRelevantSection(shouldShow);
    };

    updateVisibility();
    window.addEventListener("scroll", updateVisibility, { passive: true });
    window.addEventListener("resize", updateVisibility);

    return () => {
      window.removeEventListener("scroll", updateVisibility);
      window.removeEventListener("resize", updateVisibility);
    };
  }, [isConsultationPage, isHome, location.pathname]);

  useEffect(() => {
    if (!isConsultationPage || location.hash !== "#consultation-form") return;

    let attempts = 0;
    let timeoutId: number | undefined;

    const scrollToForm = () => {
      const form = document.getElementById("consultation-form");
      if (form) {
        form.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      attempts += 1;
      if (attempts < 12) timeoutId = window.setTimeout(scrollToForm, 50);
    };

    const frameId = window.requestAnimationFrame(scrollToForm);
    return () => {
      window.cancelAnimationFrame(frameId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [isConsultationPage, location.hash, location.pathname]);

  if ((isHome || isConsultationPage) && !hasPassedRelevantSection) return null;

  const openConsultationForm = () => {
    const form = document.getElementById("consultation-form");
    if (isConsultationPage && form) {
      form.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    navigate("/consultations#consultation-form");
  };

  return (
    <button
      type="button"
      className="floating-consultation-cta"
      onClick={openConsultationForm}
      aria-label="Перейти до форми кар'єрної консультації"
    >
      <span className="floating-consultation-cta__icon" aria-hidden="true">
        <MessageCircle size={19} strokeWidth={2.4} />
      </span>
      <span>Отримати консультацію</span>
      <ArrowUpRight className="floating-consultation-cta__arrow" size={18} aria-hidden="true" />
    </button>
  );
};

export default FloatingConsultationCta;
